<template>
  <NoticeDialog
    :open="state.open"
    :variant="state.variant"
    :mode="state.mode"
    :title="state.title"
    :confirm-tone="state.confirmTone"
    :confirm-label="state.confirmLabel || defaultConfirmLabel"
    :cancel-label="state.cancelLabel || t('common.cancel')"
    :busy="state.busy"
    @confirm="onConfirm"
    @cancel="onCancel"
    @close="onClose"
  >
    {{ state.body }}
    <!-- Hard errors carry a reference: offer a direct line to support, pre-filled
         with it. Gated by `support-enabled` — only apps that render a
         SupportRequestDialogHost turn it on, so the link is never a dead end. -->
    <template v-if="showSupport">
      <Button variant="link" class="notice-support-link" @click="contactSupport">
        {{ t('common.contactSupport') }}
      </Button>
    </template>
  </NoticeDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button, NoticeDialog } from '@lumi/ui';
import { useI18n } from 'vue-i18n';
import { useDialog } from '../composables/useDialog';
import { useSupport } from '../composables/useSupport';

// `support-enabled` gates the hard-error "contact support" affordance. It defaults
// off so the affordance only appears where a SupportRequestDialogHost is mounted to
// receive it (opt-in per app), keeping behaviour identical for apps without support.
const props = defineProps<{ supportEnabled?: boolean }>();

const { t } = useI18n();
const { state, onConfirm, onCancel, onClose } = useDialog();
const support = useSupport();

// Acknowledge dialogs default to "Verstanden"; decision dialogs that omit a
// confirm label fall back to the neutral "OK".
const defaultConfirmLabel = computed(() =>
  state.mode === 'acknowledge' ? t('common.understood') : t('common.ok'),
);

const showSupport = computed(
  () => Boolean(props.supportEnabled) && state.variant === 'error' && Boolean(state.reference),
);

function contactSupport() {
  const ref = state.reference;
  onClose();
  support.open(ref);
}
</script>

<style scoped>
.notice-support-link {
  margin-top: var(--space-3);
}
</style>
